import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Card,
  Skeleton,
  Avatar,
  Button,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import { getUserGotySelections, likeUserGotySelections, unlikeUserGotySelections } from '../services/goty_api';
import { UserGotySelectionsResponse, GotyCategory, GotyCategoryLabels } from '../types/Goty';
import { SITE_NAME } from '../constants/site';
import { getCurrentUserId } from '../services/auth';
import GotyShareMenu from '../components/GOTY/GotyShareMenu';
import GotyThumbnailGenerator from '../components/GOTY/GotyThumbnailGenerator';
import GotyGameListCopy from '../components/GOTY/GotyGameListCopy';

const UserGotyPage: React.FC = () => {
  const { username, year } = useParams<{ username: string; year: string }>();
  const location = useLocation();
  const [data, setData] = useState<UserGotySelectionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [heroImage, setHeroImage] = useState<string | null>(null);
  const [showThumbnailGenerator, setShowThumbnailGenerator] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);

  const currentYear = parseInt(year || '2025');
  const isMyGoty = location.pathname.startsWith('/mygoty/');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // ログインユーザーIDを取得
        const userId = await getCurrentUserId();
        setCurrentUserId(userId);

        if (username) {
          const response = await getUserGotySelections(username, currentYear, isMyGoty);
          setData(response);
          setLiked(response.is_liked || false);
          setLikeCount(response.like_count || 0);

          // 自分のGOTYの場合はサムネイル生成を最初から表示
          if (userId && response.user.id === userId) {
            setShowThumbnailGenerator(true);
          }
        }
      } catch (error) {
        console.error('Failed to fetch GOTY data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [username, currentYear, isMyGoty]);

  // Steam画像URLを生成（総合大賞のゲーム）
  useEffect(() => {
    if (!data?.overall_game) return;

    const steamId = data.overall_game.links?.steam;
    if (steamId) {
      const steamHeroUrl = `https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/${steamId}/library_hero.jpg`;

      const img = new Image();
      img.onload = () => {
        setHeroImage(steamHeroUrl);
      };
      img.onerror = () => {
        setHeroImage(data.overall_game?.cover_hd || null);
      };
      img.src = steamHeroUrl;
    } else {
      setHeroImage(data.overall_game?.cover_hd || null);
    }
  }, [data]);

  const getCategorySelection = (category: GotyCategory) => {
    return data?.selections.find(s => s.category === category);
  };

  const handleLikeToggle = async () => {
    if (!username) return;

    try {
      if (liked) {
        await unlikeUserGotySelections(username, currentYear, isMyGoty);
        setLiked(false);
        setLikeCount(prev => prev - 1);
      } else {
        await likeUserGotySelections(username, currentYear, isMyGoty);
        setLiked(true);
        setLikeCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Failed to toggle like:', error);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 4, borderRadius: 2 }} />
        <Skeleton variant="text" width="60%" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Container>
    );
  }

  const pageTitle = data
    ? `${data.user.display_name || username || 'User'}'s ${isMyGoty ? 'MyGOTY' : 'GOTY'} ${currentYear} | ${SITE_NAME}`
    : `GOTY ${currentYear} | ${SITE_NAME}`;

  const overallSelection = getCategorySelection(GotyCategory.OVERALL);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', overflowX: 'hidden' }}>
        {/* ヒーローセクション - 背景画像のみ */}
        <Box
          sx={{
            position: 'relative',
            height: { xs: 300, sm: 400, md: 500, lg: 600 },
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${heroImage || '/image/no_image.png'})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
              filter: heroImage && data?.overall_game?.links?.steam ? 'none' : 'blur(40px) brightness(0.6)',
              transform: heroImage && data?.overall_game?.links?.steam ? 'none' : 'scale(1.1)',
            }
          }}
        >
          {/* グラデーションオーバーレイ */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 50%, #121212 100%)',
            }}
          />
        </Box>

        {/* 年号セクション */}
        <Container maxWidth="lg" sx={{
          position: 'relative',
          zIndex: 1,
          mt: heroImage && data?.overall_game?.links?.steam
            ? { xs: -15, md: -25, lg: -40 }
            : { xs: -15, md: -40, lg: -55 },
          mb: -2
        }}>
          <Box sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              {data?.user.avatar && (
                <Avatar src={data.user.avatar} sx={{ width: 36, height: 36 }} />
              )}
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                {data?.user.display_name}
              </Typography>
            </Box>

            <Typography
              variant="caption"
              sx={{
                color: 'rgba(218, 218, 218, 0.8)',
                textTransform: 'uppercase',
                letterSpacing: 2,
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: 600,
                mb: 0
              }}
            >
              {isMyGoty ? 'My Game of the Year' : 'Game of the Year'}
            </Typography>

            <Typography
              variant="h1"
              sx={{
                color: '#c2c2c2ff',
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '3.5rem' },
                fontWeight: 900,
                lineHeight: 1.2,
                textShadow: '0 4px 20px rgba(0,0,0,0.8)'
              }}
            >
              {currentYear}
            </Typography>
          </Box>
        </Container>

        {/* メインコンテンツ */}
        <Container maxWidth="lg" sx={{ mt: 0, position: 'relative', zIndex: 1, pb: 8 }}>
          {/* 総合大賞カード - 中央に1枚 */}
          {overallSelection && data?.overall_game && (
            <Box sx={{ mb: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
              {/* ペルソナ風の装飾背景 */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -40,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: { xs: '100%', md: '150%' },
                  height: '120%',
                  background: 'radial-gradient(ellipse at center, rgba(102, 126, 234, 0.15) 0%, transparent 70%)',
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              />

              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  fontFamily: 'arial, sans-serif',
                  mb: 4,
                  color: '#ffffffff',
                  textAlign: 'center',
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '3.5rem' },
                  textShadow: '0 0 30px rgba(102, 126, 234, 0.5)',
                  position: 'relative',
                  zIndex: 1,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    bottom: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '60%',
                    height: 4,
                    background: 'linear-gradient(90deg, transparent, #667eea, transparent)',
                  }
                }}
              >
                {isMyGoty ? 'MyGOTY' : 'WINNER'} 
              </Typography>

              <Card
                component={RouterLink}
                to={`/game/${overallSelection.game.game_id}`}
                sx={{
                  position: 'relative',
                  background: 'transparent',
                  border: 'none',
                  overflow: 'visible',
                  maxWidth: { xs: 250, md: 400 },
                  width: '100%',
                  textDecoration: 'none',
                  zIndex: 1,
                }}
              >
                <Box
                  className="game-cover"
                  sx={{
                    position: 'relative',
                    paddingTop: '133.33%',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 24px 80px rgba(102, 126, 234, 0.1), 0 0 60px rgba(102, 126, 234, 0.3)',
                    border: '6px solid',
                    borderColor: 'rgba(102, 126, 234, 0.3)',
                  }}
                >
                  <Box
                    component="img"
                    alt={overallSelection.game.name}
                    src={data.overall_game.cover_hd || data.overall_game.cover || '/image/no_image.png'}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  {/* グロー効果 */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(180deg, rgba(102, 126, 234, 0.1) 0%, transparent 50%)',
                      pointerEvents: 'none',
                    }}
                  />
                </Box>
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 2, fontSize: { xs: '1.1rem', md: '1.3rem' }, lineHeight: 1.3 }}>
                    {overallSelection.game.japanese_name || overallSelection.game.name}
                  </Typography>
                  {overallSelection.comment && (
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', fontStyle: 'italic', fontSize: '0.8rem', lineHeight: 1.25, maxWidth: 450, mx: 'auto' }}>
                      "{overallSelection.comment}"
                    </Typography>
                  )}
                </Box>
              </Card>
            </Box>
          )}

          {/* その他のカテゴリ - グリッド表示 */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 6 }}>
            {[GotyCategory.VISUAL, GotyCategory.SOUND, GotyCategory.GAMEPLAY, GotyCategory.NARRATIVE, GotyCategory.ORIGINALITY, GotyCategory.MULTIPLAYER, GotyCategory.INDIE, GotyCategory.REMAKE, GotyCategory.ACCESSIBILITY].map((category) => {
              const selection = getCategorySelection(category);
              const label = GotyCategoryLabels[category];

              return (
                <Box key={category} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                  {/* 背景の光エフェクト */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -40,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: { xs: '100%', md: '200%' },
                      height: '120%',
                      background: 'radial-gradient(ellipse at center, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 40%, transparent 70%)',
                      pointerEvents: 'none',
                      zIndex: 0,
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      mb: 3,
                      color: 'rgba(255,255,255,0.8)',
                      textAlign: 'center',
                      fontSize: '1.1rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      position: 'relative',
                      zIndex: 1,
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '80%',
                        height: 3,
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                      }
                    }}
                  >
                    {label.ja}
                  </Typography>
                  {selection?.game ? (
                    <Card
                      component={RouterLink}
                      to={`/game/${selection.game.game_id}`}
                      sx={{
                        background: 'transparent',
                        border: 'none',
                        overflow: 'visible',
                        width: '100%',
                        maxWidth: 250,
                        textDecoration: 'none',
                        zIndex: 1,
                      }}
                    >
                      <Box
                        className="game-cover"
                        sx={{
                          position: 'relative',
                          paddingTop: '133.33%',
                          borderRadius: 2,
                          overflow: 'hidden',
                          boxShadow: '0 6px 24px rgba(0,0,0,0.6)',
                          border: '2px solid',
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        }}
                      >
                        <Box
                          component="img"
                          src={selection.game.cover || '/image/no_image.png'}
                          alt={selection.game.name}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        {/* グロー効果 */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.03) 0%, transparent 50%)',
                            pointerEvents: 'none',
                          }}
                        />
                      </Box>
                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: 'white', mb: 0.5, fontSize: '0.8rem', lineHeight: 1.3 }}>
                          {selection.game.japanese_name || selection.game.name}
                        </Typography>
                        {selection.comment && (
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontStyle: 'italic', fontSize: '0.75rem', lineHeight: 1.25 }}>
                            "{selection.comment}"
                          </Typography>
                        )}
                      </Box>
                    </Card>
                  ) : (
                    <Box sx={{
                      width: '100%',
                      maxWidth: 250,
                      aspectRatio: '3/4',
                      position: 'relative',
                      borderRadius: 2,
                      border: '2px dashed',
                      borderColor: 'rgba(102, 126, 234, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(102, 126, 234, 0.03)',
                    }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(102, 126, 234, 0.3)',
                          textAlign: 'center',
                          fontWeight: 500,
                        }}
                      >
                        未選択
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* シェアセクション - 最下部 */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            mt: 8,
            mb: 4
          }}>
            {/* サムネイル生成セクション - 自分のGOTYのみ */}
            {showThumbnailGenerator && data && username && (
              <GotyThumbnailGenerator
                data={data}
                year={currentYear}
                isMyGoty={isMyGoty}
              />
            )}

            {/* ゲームリストコピーとシェアボタン - 2カラム（PCのみ） */}
            {data && username && (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2,
                width: '100%',
                maxWidth: '800px'
              }}>
                <GotyShareMenu
                  username={username}
                  year={currentYear}
                  isMyGoty={isMyGoty}
                  displayName={data.user.display_name}
                  gameName={data.overall_game?.japanese_name || data.overall_game?.name}
                  onThumbnailClick={() => setShowThumbnailGenerator(!showThumbnailGenerator)}
                  showThumbnailOption={currentUserId !== data.user.id}
                />
                <GotyGameListCopy
                  data={data}
                />
              </Box>
            )}

            {/* フッター - いいねと日時 */}
            {data && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                width: '100%',
                maxWidth: '800px',
                pt: 2,
                borderTop: '1px solid rgba(255, 255, 255, 0.12)'
              }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={liked ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
                  onClick={handleLikeToggle}
                  sx={{
                    minWidth: '80px',
                    borderColor: liked ? 'rgba(102, 126, 234, 0.8)' : 'rgba(255, 255, 255, 0.2)',
                    backgroundColor: liked ? 'rgba(102, 126, 234, 0.15)' : 'transparent',
                    color: liked ? 'rgba(102, 126, 234, 1)' : 'rgba(255, 255, 255, 0.7)',
                    '&:hover': {
                      borderColor: 'rgba(102, 126, 234, 0.8)',
                      backgroundColor: 'rgba(102, 126, 234, 0.2)',
                      color: 'rgba(102, 126, 234, 1)'
                    }
                  }}
                >
                  {likeCount}
                </Button>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem'
                  }}
                >
                  最終更新：{(() => {
                    const latestDate = data.selections.reduce((latest, selection) => {
                      const currentDate = new Date(selection.created_at);
                      return currentDate > latest ? currentDate : latest;
                    }, new Date(data.selections[0]?.created_at || Date.now()));
                    return latestDate.toLocaleDateString('ja-JP', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit'
                    });
                  })()}
                </Typography>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default UserGotyPage;
